import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Bot, Send, Scale, Calculator, Building2, Home, Target, Landmark,
  GraduationCap, Megaphone, FileText, Cpu, CreditCard, AlertTriangle,
  User, Phone, Mail, MessageSquare, ChevronRight, History, Loader2,
  MapPin, CheckCircle2, Shield, Crown, ArrowRight, Sparkles, X, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iconos por área
const AREA_ICONS = {
  legal_inmobiliario: Scale,
  fiscal_inmobiliario: Calculator,
  financiera_inmobiliaria: Target,
  patrimonial: Landmark,
  comercial_industrial: Building2,
  desarrollo_inmobiliario: Home,
  valuacion: Calculator,
  hipotecario: Home,
  infonavit: CreditCard,
  fovissste: Building2,
  academica: GraduationCap,
  capacitacion: GraduationCap,
  marketing_inmobiliario: Megaphone,
  especializacion_ventas: Target,
  nuevos_negocios: Target,
  notarial: FileText,
  tecnologia_ia: Cpu
};

// Estados de México
const ESTADOS_MEXICO = [
  { value: 'aguascalientes', label: 'Aguascalientes' },
  { value: 'baja_california', label: 'Baja California' },
  { value: 'baja_california_sur', label: 'Baja California Sur' },
  { value: 'campeche', label: 'Campeche' },
  { value: 'chiapas', label: 'Chiapas' },
  { value: 'chihuahua', label: 'Chihuahua' },
  { value: 'cdmx', label: 'Ciudad de México' },
  { value: 'coahuila', label: 'Coahuila' },
  { value: 'colima', label: 'Colima' },
  { value: 'durango', label: 'Durango' },
  { value: 'estado_de_mexico', label: 'Estado de México' },
  { value: 'guanajuato', label: 'Guanajuato' },
  { value: 'guerrero', label: 'Guerrero' },
  { value: 'hidalgo', label: 'Hidalgo' },
  { value: 'jalisco', label: 'Jalisco' },
  { value: 'michoacan', label: 'Michoacán' },
  { value: 'morelos', label: 'Morelos' },
  { value: 'nayarit', label: 'Nayarit' },
  { value: 'nuevo_leon', label: 'Nuevo León' },
  { value: 'oaxaca', label: 'Oaxaca' },
  { value: 'puebla', label: 'Puebla' },
  { value: 'queretaro', label: 'Querétaro' },
  { value: 'quintana_roo', label: 'Quintana Roo' },
  { value: 'san_luis_potosi', label: 'San Luis Potosí' },
  { value: 'sinaloa', label: 'Sinaloa' },
  { value: 'sonora', label: 'Sonora' },
  { value: 'tabasco', label: 'Tabasco' },
  { value: 'tamaulipas', label: 'Tamaulipas' },
  { value: 'tlaxcala', label: 'Tlaxcala' },
  { value: 'veracruz', label: 'Veracruz' },
  { value: 'yucatan', label: 'Yucatán' },
  { value: 'zacatecas', label: 'Zacatecas' }
];

const AsesoriasEspecializadasPage = () => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const responseRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedEstado, setSelectedEstado] = useState('');
  const [pregunta, setPregunta] = useState('');
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [showHistorial, setShowHistorial] = useState(false);

  useEffect(() => {
    loadAreas();
    if (isAuthenticated) {
      loadHistorial();
    }
  }, [isAuthenticated]);

  const loadAreas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/institutional/areas`);
      setAreas(res.data.areas || []);
    } catch (error) {
      console.error('Error loading areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistorial = async () => {
    try {
      const token = localStorage.getItem('upim_token');
      const res = await axios.get(`${API_URL}/api/institutional/mis-consultas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorial(res.data.consultas || []);
    } catch (error) {
      console.error('Error loading historial:', error);
    }
  };

  const handleSendQuestion = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para consultar a los bots especializados",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    if (!selectedArea || !pregunta.trim()) {
      toast({
        title: "Completa los campos",
        description: "Selecciona un área y escribe tu pregunta",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    setResponse(null);

    try {
      const token = localStorage.getItem('upim_token');
      const res = await axios.post(
        `${API_URL}/api/institutional/consulta`,
        {
          area_id: selectedArea.id,
          pregunta: pregunta.trim(),
          estado: selectedEstado || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResponse(res.data);
      setPregunta('');
      loadHistorial();
      
      // Scroll to response
      setTimeout(() => {
        responseRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error sending question:', error);
      toast({
        title: "Error",
        description: "No se pudo procesar tu consulta. Intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const AreaCard = ({ area }) => {
    const IconComponent = AREA_ICONS[area.codigo] || Bot;
    const isSelected = selectedArea?.id === area.id;
    
    return (
      <button
        onClick={() => setSelectedArea(area)}
        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
          isSelected 
            ? 'border-indigo-500 bg-indigo-50 shadow-md' 
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
        }`}
        data-testid={`area-card-${area.codigo}`}
      >
        <div className="flex items-start gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${area.color_primario}15` }}
          >
            <IconComponent className="w-6 h-6" style={{ color: area.color_primario }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{area.nombre}</h3>
            <p className="text-sm text-gray-600 line-clamp-2">{area.descripcion}</p>
            {area.bot_nombre && (
              <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                <Bot className="w-3 h-3" />
                {area.bot_nombre}
              </p>
            )}
          </div>
          {isSelected && (
            <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
          )}
        </div>
      </button>
    );
  };

  const DisclaimerBanner = () => (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-amber-800 mb-1">Aviso Importante</h4>
          <p className="text-sm text-amber-700">
            Las respuestas de UPIM IA son orientativas y se basan en normativa mexicana vigente. 
            <strong> Para validar esta información, deberás consultar al Director de Área correspondiente.</strong>
            {' '}UPIM no sustituye asesoría legal, fiscal ni notarial profesional.
          </p>
        </div>
      </div>
    </div>
  );

  const DirectorContact = ({ director }) => (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
      <div className="flex items-center gap-2 mb-3">
        <Crown className="w-5 h-5 text-indigo-600" />
        <h4 className="font-semibold text-indigo-900">Contacta al Director para Validar</h4>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center">
          <User className="w-7 h-7 text-gray-400" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-900">{director.nombre}</p>
          <p className="text-sm text-indigo-600">{director.titulo}</p>
          <Badge variant="outline" className="mt-1 text-xs">
            Director {director.nivel === 'nacional' ? 'Nacional' : 'Regional'}
          </Badge>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {director.whatsapp && (
          <a
            href={`https://wa.me/${director.whatsapp.replace(/\D/g, '')}?text=Hola, consulté con UPIM IA y me gustaría validar la información.`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            WhatsApp
          </a>
        )}
        {director.email && (
          <a
            href={`mailto:${director.email}?subject=Validación de consulta UPIM IA`}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            Email
          </a>
        )}
      </div>
    </div>
  );

  const NoDirectorMessage = () => (
    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 text-center">
      <div className="w-12 h-12 mx-auto bg-amber-100 rounded-xl flex items-center justify-center mb-3">
        <AlertTriangle className="w-6 h-6 text-amber-600" />
      </div>
      <h4 className="font-semibold text-gray-900 mb-2">Director no asignado</h4>
      <p className="text-sm text-gray-600 mb-4">
        Actualmente no hay un Director asignado a esta área. Te recomendamos verificar la información con un profesional certificado.
      </p>
      <Button variant="outline" onClick={() => navigate('/directorio')}>
        Ver Directorio UPIM
        <ExternalLink className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50" data-testid="asesorias-especializadas-page">
        {/* Hero */}
        <div className="bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-yellow-500 text-yellow-900 mb-4">
                <Sparkles className="w-4 h-4 mr-1" />
                UPIM IA
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                Asesorías Especializadas
              </h1>
              <p className="text-lg text-gray-300">
                Consulta con nuestros bots IA especializados en diferentes áreas del sector inmobiliario mexicano. 
                Cada respuesta incluye fundamentos legales y será validada por un Director de Área.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Disclaimer */}
          <DisclaimerBanner />

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Sidebar - Áreas */}
            <div className="lg:col-span-1 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold text-gray-900">Áreas Especializadas</h2>
                {historial.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowHistorial(!showHistorial)}
                    className="text-indigo-600"
                  >
                    <History className="w-4 h-4 mr-1" />
                    Historial
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {areas.map(area => (
                    <AreaCard key={area.id} area={area} />
                  ))}
                </div>
              )}
            </div>

            {/* Main Content - Chat */}
            <div className="lg:col-span-2">
              {selectedArea ? (
                <Card className="border-2">
                  <CardHeader className="border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${selectedArea.color_primario}15` }}
                      >
                        {React.createElement(AREA_ICONS[selectedArea.codigo] || Bot, {
                          className: "w-6 h-6",
                          style: { color: selectedArea.color_primario }
                        })}
                      </div>
                      <div>
                        <CardTitle>{selectedArea.bot_nombre || `Bot ${selectedArea.nombre}`}</CardTitle>
                        <CardDescription>Área de {selectedArea.nombre}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    {/* Selector de Estado */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        Estado de México (opcional, para normativa local)
                      </label>
                      <select
                        value={selectedEstado}
                        onChange={(e) => setSelectedEstado(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        data-testid="estado-selector"
                      >
                        <option value="">-- Selecciona un estado --</option>
                        {ESTADOS_MEXICO.map(estado => (
                          <option key={estado.value} value={estado.value}>{estado.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Textarea para pregunta */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tu pregunta o consulta
                      </label>
                      <Textarea
                        value={pregunta}
                        onChange={(e) => setPregunta(e.target.value)}
                        placeholder={`Escribe tu consulta sobre ${selectedArea.nombre}...`}
                        className="min-h-[120px]"
                        data-testid="pregunta-input"
                      />
                    </div>

                    {/* Botón enviar */}
                    <Button
                      onClick={handleSendQuestion}
                      disabled={!pregunta.trim() || sending}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid="send-question-btn"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Consultando al bot...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Enviar Consulta
                        </>
                      )}
                    </Button>

                    {/* Respuesta */}
                    {response && (
                      <div ref={responseRef} className="space-y-6 pt-6 border-t">
                        {/* Respuesta IA */}
                        <div className="bg-white rounded-xl border p-5">
                          <div className="flex items-center gap-2 mb-3">
                            <Bot className="w-5 h-5 text-indigo-600" />
                            <h4 className="font-semibold text-gray-900">Respuesta de {selectedArea.bot_nombre}</h4>
                          </div>
                          <div className="prose prose-sm max-w-none">
                            <p className="text-gray-700 whitespace-pre-wrap">{response.respuesta}</p>
                          </div>
                        </div>

                        {/* Disclaimer obligatorio */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <div className="text-sm text-amber-800">
                              <strong>AVISO IMPORTANTE:</strong> Esta respuesta fue generada por Inteligencia Artificial (UPIM IA). 
                              Aunque se basa en normativa vigente y criterios profesionales, puede contener errores o requerir interpretación.
                              <strong> Para validar esta información, consulte al Director de Área correspondiente.</strong>
                            </div>
                          </div>
                        </div>

                        {/* Contacto Director */}
                        {response.director_asignado ? (
                          <DirectorContact director={response.director_asignado} />
                        ) : (
                          <NoDirectorMessage />
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
                  <div className="w-16 h-16 mx-auto bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                    <Bot className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Selecciona un Área</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Elige un área especializada de la lista para comenzar tu consulta con el bot de IA correspondiente.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Historial Modal */}
          {showHistorial && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
                <CardHeader className="border-b flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Historial de Consultas
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setShowHistorial(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </CardHeader>
                <CardContent className="p-0 overflow-y-auto max-h-[60vh]">
                  {historial.length > 0 ? (
                    <div className="divide-y">
                      {historial.map((consulta) => (
                        <div key={consulta.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{consulta.pregunta}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {consulta.status === 'validado' ? 'Validado' : 'Pendiente validación'}
                                </Badge>
                                {consulta.estado && (
                                  <span className="text-xs text-gray-500">
                                    <MapPin className="w-3 h-3 inline" /> {consulta.estado}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="text-xs text-gray-400">
                              {consulta.created_at && new Date(consulta.created_at).toLocaleDateString('es-MX')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      No tienes consultas previas
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gray-900 py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              ¿Necesitas asesoría más completa?
            </h2>
            <p className="text-gray-400 mb-6">
              Accede a UPIM IA completo con chat continuo, todos los agentes especializados y funciones avanzadas.
            </p>
            <Button 
              size="lg" 
              className="bg-yellow-500 text-gray-900 hover:bg-yellow-400"
              onClick={() => navigate('/upim-ia/chat')}
            >
              Ir a UPIM IA Chat
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AsesoriasEspecializadasPage;
